import { useState, useCallback, useEffect, useRef } from "react";
import { SessionState, WsEvent, ScoreVisibility } from "../types/game";
import { useWebSocket, WsStatus } from "./useWebSocket";
import { normalizeCode } from "../utils/inviteCode";

const API = import.meta.env.VITE_API_URL || "/api/v1";

interface QueuedUpdate {
  player_id: string;
  category: string;
  value: number | null;
  from_hand: boolean;
}

async function apiFetch<T>(
  path: string,
  token: string,
  opts: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(opts.headers ?? {}),
    },
  });
  if (!res.ok) {
    let detail = await res.text().catch(() => res.statusText);
    try {
      const parsed = JSON.parse(detail);
      if (parsed.detail) detail = parsed.detail;
    } catch {
      // not json
    }
    throw new Error(detail);
  }
  return res.json();
}

export function useGameSession(token: string | null, onEvent?: (event: WsEvent) => void) {
  const [state, setState] = useState<SessionState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queuedUpdates, setQueuedUpdates] = useState<QueuedUpdate[]>([]);
  const [suggestions, setSuggestions] = useState<Record<string, number>>({});
  const isFlushingRef = useRef(false);

  const joinByCode = useCallback(
    async (code: string) => {
      if (!token) return;
      setLoading(true);
      setError(null);
      setSuggestions({});
      try {
        // POST /join seats the caller as a player automatically (falls back to
        // spectator state only when the game is finished or full)
        const full = await apiFetch<SessionState>(
          `/sessions/${normalizeCode(code)}/join`,
          token,
          { method: "POST" },
        );
        setState(full);
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  const fetchById = useCallback(
    async (sessionId: string) => {
      if (!token) return;
      setLoading(true);
      setError(null);
      setSuggestions({});
      try {
        const full = await apiFetch<SessionState>(
          `/sessions/by-id/${sessionId}`,
          token,
        );
        setState(full);
      } catch {
        setError("Historical record not found");
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  const handleWsEvent = useCallback((event: WsEvent) => {
    // Call external listener if provided
    if (onEvent) onEvent(event);

    switch (event.type) {
      case "session_state":
        // Defensive: a malformed event must not replace state with undefined
        // fields — every view dereferences state.session.* and would crash.
        if (event.session && event.players) {
          setState({
            session: event.session,
            players_with_scorecards: event.players,
          });
        }
        break;

      case "game_started":
        if (event.state?.session && event.state?.players_with_scorecards) {
          setState(event.state);
        }
        break;

      case "score_updated":
        setState((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            players_with_scorecards: prev.players_with_scorecards.map((pwsc) => {
              if (pwsc.player.id !== event.player_id) return pwsc;
              // Keep the filled-categories list in sync so round counters and
              // locks work on redacted (final_only) payloads.
              const nowFilled = event.value !== null;
              const filled =
                pwsc.filled !== undefined
                  ? nowFilled
                    ? Array.from(new Set([...pwsc.filled, event.category]))
                    : pwsc.filled.filter((c) => c !== event.category)
                  : pwsc.filled;
              const marks = (pwsc.scorecard.from_hand ?? []).filter(
                (c) => c !== event.category,
              );
              if (event.from_hand && event.value !== null) marks.push(event.category);
              return {
                ...pwsc,
                scorecard: {
                  ...pwsc.scorecard,
                  [event.category]: event.value,
                  from_hand: marks,
                },
                totals: event.totals,
                filled,
              };
            }),
          };
        });
        break;

      case "player_added":
      case "player_removed":
        if (event.state?.session && event.state?.players_with_scorecards) {
          setState(event.state);
        }
        break;

      case "settings_updated":
        setState((prev) =>
          prev
            ? {
                ...prev,
                session: {
                  ...prev.session,
                  score_visibility: event.score_visibility,
                },
              }
            : prev,
        );
        break;

      case "game_finished":
        setState((prev) =>
          prev
            ? { ...prev, session: { ...prev.session, status: "finished" } }
            : prev,
        );
        break;

      case "rematch":
        // Automatically join the new game session when admin hits Rematch!
        joinByCode(event.new_invite_code);
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinByCode]);

  const { wsStatus } = useWebSocket(
    state?.session.id ? `/ws/${state.session.id}` : null,
    token,
    handleWsEvent,
  );

  // Auto-flush queue on reconnect
  useEffect(() => {
    if (wsStatus === "connected" && queuedUpdates.length > 0 && !isFlushingRef.current && token && state) {
      const flush = async () => {
        isFlushingRef.current = true;
        const updates = [...queuedUpdates];
        // Process sequentially
        for (const update of updates) {
          try {
            await apiFetch(
              `/sessions/${state.session.id}/scorecards/${update.player_id}`,
              token,
              { method: "PATCH", body: JSON.stringify({ category: update.category, value: update.value, from_hand: update.from_hand }) },
            );
            // Remove successfully processed update
            setQueuedUpdates(prev => prev.filter(u => u !== update));
          } catch (e) {
            console.error("Failed to flush queued update:", e);
            // Stop flushing on error (e.g. still having network issues despite 'connected' status)
            break;
          }
        }
        isFlushingRef.current = false;
      };
      flush();
    }
  }, [wsStatus, queuedUpdates, token, state]);

  const createSession = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const session = await apiFetch<any>("/sessions", token, {
        method: "POST",
      });
      const full = await apiFetch<SessionState>(
        `/sessions/${session.invite_code}`,
        token,
      );
      setState(full);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const addRealPlayer = useCallback(
    async (user_id: string, display_name?: string) => {
      if (!token || !state) return;
      setError(null);
      try {
        const next = await apiFetch<SessionState>(
          `/sessions/${state.session.id}/players/real`,
          token,
          {
            method: "POST",
            body: JSON.stringify({ user_id, display_name }),
          },
        );
        setState(next);
      } catch (e: any) {
        setError(e.message);
      }
    },
    [token, state],
  );

  const addPhantomPlayer = useCallback(
    async (display_name: string, avatar?: string) => {
      if (!token || !state) return;
      setError(null);
      try {
        const next = await apiFetch<SessionState>(
          `/sessions/${state.session.id}/players/phantom`,
          token,
          { method: "POST", body: JSON.stringify({ display_name, avatar }) },
        );
        setState(next);
      } catch (e: any) {
        setError(e.message);
      }
    },
    [token, state],
  );

  const removePlayer = useCallback(
    async (player_id: string) => {
      if (!token || !state) return;
      setError(null);
      try {
        const next = await apiFetch<SessionState>(
          `/sessions/${state.session.id}/players/${player_id}`,
          token,
          { method: "DELETE" },
        );
        setState(next);
      } catch (e: any) {
        setError(e.message);
      }
    },
    [token, state],
  );

  const updateScore = useCallback(
    async (player_id: string, category: string, value: number | null, fromHand: boolean = false) => {
      if (!token || !state) return;
      const from_hand = fromHand && value !== null;
      // Optimistic update for instant UI feedback
      setState((prev) =>
        prev
          ? {
              ...prev,
              players_with_scorecards: prev.players_with_scorecards.map(
                (pwsc) => {
                  if (pwsc.player.id !== player_id) return pwsc;
                  const marks = (pwsc.scorecard.from_hand ?? []).filter(
                    (c) => c !== category,
                  );
                  if (from_hand) marks.push(category);
                  return {
                    ...pwsc,
                    scorecard: { ...pwsc.scorecard, [category]: value, from_hand: marks },
                  };
                },
              ),
            }
          : prev,
      );

      if (wsStatus !== "connected") {
        setQueuedUpdates(prev => [...prev, { player_id, category, value, from_hand }]);
        return;
      }

      try {
        await apiFetch(
          `/sessions/${state.session.id}/scorecards/${player_id}`,
          token,
          { method: "PATCH", body: JSON.stringify({ category, value, from_hand }) },
        );
      } catch (e: any) {
        setError(e.message);
        // If it failed because of connection, queue it anyway
        if (e.message.includes("Failed to fetch") || e.message.includes("NetworkError")) {
          setQueuedUpdates(prev => [...prev, { player_id, category, value, from_hand }]);
        }
      }
    },
    [token, state, wsStatus],
  );

  const startGame = useCallback(async () => {
    if (!token || !state) return;
    setError(null);
    try {
      await apiFetch(`/sessions/${state.session.id}/start`, token, {
        method: "POST",
      });
    } catch (e: any) {
      setError(e.message);
    }
  }, [token, state]);

  const updateSettings = useCallback(
    async (score_visibility?: ScoreVisibility, game_mode?: string) => {
      if (!token || !state) return;
      setError(null);
      try {
        const body: any = {};
        if (score_visibility) body.score_visibility = score_visibility;
        if (game_mode) body.game_mode = game_mode;

        await apiFetch(`/sessions/${state.session.id}/settings`, token, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
      } catch (e: any) {
        setError(e.message);
      }
    },
    [token, state],
  );

  const finishGame = useCallback(async () => {
    if (!token || !state) return;
    setError(null);
    try {
      await apiFetch(`/sessions/${state.session.id}/finish`, token, {
        method: "POST",
      });
    } catch (e: any) {
      setError(e.message);
    }
  }, [token, state]);

  const rematchGame = useCallback(async () => {
    if (!token || !state) return;
    setError(null);
    try {
      await apiFetch(`/sessions/${state.session.id}/rematch`, token, {
        method: "POST",
      });
    } catch (e: any) {
      setError(e.message);
    }
  }, [token, state]);

  const leaveGame = useCallback(() => {
    setState(null);
    setSuggestions({});
  }, []);

  const sendReaction = useCallback(async (emoji: string) => {
    if (!token || !state) return;
    try {
      await apiFetch(`/sessions/${state.session.id}/react`, token, {
        method: "POST",
        body: JSON.stringify({ emoji }),
      });
    } catch (e: any) {
      console.error("Failed to send reaction", e);
    }
  }, [token, state]);

  const rollDice = useCallback(async () => {
    if (!token || !state) return;
    try {
      const next = await apiFetch<SessionState>(
        `/sessions/${state.session.id}/roll`,
        token,
        { method: "POST" }
      );
      setState(next);
    } catch (e: any) {
      setError(e.message);
    }
  }, [token, state]);

  const toggleHold = useCallback(async (index: number) => {
    if (!token || !state) return;
    try {
      const next = await apiFetch<SessionState>(
        `/sessions/${state.session.id}/holds?index=${index}`,
        token,
        { method: "PATCH" }
      );
      setState(next);
    } catch (e: any) {
      setError(e.message);
    }
  }, [token, state]);

  const fetchSuggestions = useCallback(async () => {
    if (!token || !state) return;
    try {
      const data = await apiFetch<Record<string, number>>(
        `/sessions/${state.session.id}/suggestions`,
        token
      );
      setSuggestions(data);
    } catch (e: any) {
      console.error("Failed to fetch suggestions", e);
    }
  }, [token, state]);

  // Fetch suggestions automatically when dice changes in strict mode
  useEffect(() => {
    if (state?.session.game_mode === "strict" && state.session.current_dice && state.session.roll_count > 0) {
      fetchSuggestions();
    } else {
      setSuggestions({});
    }
  }, [state?.session.current_dice, state?.session.roll_count, state?.session.game_mode, fetchSuggestions]);

  const notifyResults = useCallback(async () => {
    if (!token || !state) return;
    try {
      await apiFetch(`/sessions/${state.session.id}/notify-results`, token, {
        method: "POST",
      });
    } catch (e: any) {
      setError(e.message);
    }
  }, [token, state]);

  const reorderPlayers = useCallback(async (orderedPlayerIds: string[]) => {
    if (!token || !state) return;
    // Optimistic update: reorder locally immediately
    setState(prev => {
      if (!prev) return prev;
      const byId = Object.fromEntries(prev.players_with_scorecards.map(p => [p.player.id, p]));
      const reordered = orderedPlayerIds
        .map(id => byId[id])
        .filter(Boolean);
      return { ...prev, players_with_scorecards: reordered };
    });
    try {
      await apiFetch(`/sessions/${state.session.id}/players/reorder`, token, {
        method: "POST",
        body: JSON.stringify({ ordered_player_ids: orderedPlayerIds }),
      });
    } catch (e: any) {
      setError(e.message);
    }
  }, [token, state]);

  const updatePlayerName = useCallback(async (pid: string, name: string) => {
    if (!token || !state) return;
    // Optimistic update
    setState(prev => {
        if (!prev) return prev;
        return {
            ...prev,
            players_with_scorecards: prev.players_with_scorecards.map(pwsc => 
                pwsc.player.id === pid 
                    ? { ...pwsc, player: { ...pwsc.player, display_name: name } }
                    : pwsc
            )
        };
    });
    try {
        await apiFetch(`/sessions/${state.session.id}/players/${pid}`, token, {
            method: "PATCH",
            body: JSON.stringify({ display_name: name })
        });
    } catch (e: any) {
        setError(e.message);
    }
  }, [token, state]);

  return {
    state,
    loading,
    error,
    wsStatus: wsStatus as WsStatus,
    queuedCount: queuedUpdates.length,
    suggestions,
    createSession,
    joinByCode,
    fetchById,
    addRealPlayer,
    addPhantomPlayer,
    removePlayer,
    updateScore,
    updateSettings,
    finishGame,
    startGame,
    rematchGame,
    rollDice,
    toggleHold,
    sendReaction,
    reorderPlayers,
    updatePlayerName,
    leaveGame,
    notifyResults,
    setError,
  };
}
